import { AsyncPipe, CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnInit } from '@angular/core';
import type { DealTypeFormInterface } from '@zaoblako/shared/modules/business/dto/deal-type/deal-type.form.dto';
import { DealTypeFormDto } from '@zaoblako/shared/modules/business/dto/deal-type/deal-type.form.dto';
import { FormsModule } from '@angular/forms';
import { JoinPipe } from '@zaoblako/web/shared/pipes/join.pipe';
import type { FormType } from '@zaoblako/web/shared/components/form-sidebar/models/form-state';
import { FormStoreService } from '@zaoblako/web/shared/components/form-sidebar/services/form-store.service';
import { BaseFormComponent } from '@zaoblako/web/shared/dumbs/base-form/base-form.component';
import { ButtonComponent } from '@zaoblako/web/shared/dumbs/button/button.component';
import { CompanySingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/company-single-select/company-single-select.component';
import { NomenclatureMultiSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/nomenclature-multi-select/nomenclature-multi-select.component';
import { UserGroupMultiSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/user-group-multi-select/user-group-multi-select.component';
import { UserMultiSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/user-multi-select/user-multi-select.component';
import { UserSingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/user-single-select/user-single-select.component';
import { EavEntityTypeSingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/eav-entity-type-single-select/eav-entity-type-single-select.component';
import { InputTextComponent } from '@zaoblako/web/shared/dumbs/input-text/input-text.component';
import { SpinnerComponent } from '@zaoblako/web/shared/dumbs/spinner/spinner.component';
import { Observable } from 'rxjs';
import { DealTypeService } from '@zaoblako/web/core/services/deal/deal-type.service';
import { NomenclatureTreeService } from '@zaoblako/web/core/services/finance/nomenclature-tree.service';
import type { NomenclatureTreeFindInterface } from '@zaoblako/shared/modules/catalog/dtos/nomenclature/nomenclature-tree-find.dto';
import { ListStyleEnum } from '@zaoblako/shared/common/enums/list-style.enum';
import { ErrorService } from '@zaoblako/web/core/services/error.service';
import { ConfirmDialogService } from '@zaoblako/web/core/services/confirm-dialog.service';
import { CardModule } from 'primeng/card';

@Component({
  selector: 'crm-deal-type-form',
  standalone: true,
  imports: [
    CommonModule,
    AsyncPipe,
    FormsModule,
    SpinnerComponent,
    InputTextComponent,
    ButtonComponent,
    CardModule,
    CompanySingleSelectComponent,
    NomenclatureMultiSelectComponent,
    UserSingleSelectComponent,
    UserMultiSelectComponent,
    UserGroupMultiSelectComponent,
    EavEntityTypeSingleSelectComponent,
    JoinPipe
  ],
  templateUrl: './deal-type-form.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class DealTypeFormComponent extends BaseFormComponent<DealTypeFormInterface> implements OnInit {
  @Input() public readonly index: number;

  public dealType: DealTypeFormInterface & { id?: number; deleted?: Date | null } = {
    name: ''
  };

  public nomenclatures: NomenclatureTreeFindInterface[] = [];

  public dto = DealTypeFormDto;
  public form$: Observable<FormType>;
  public loading$: Observable<boolean>;
  public constructor(
    private readonly dealTypeService: DealTypeService,
    private readonly nomenclatureTreeService: NomenclatureTreeService,
    public readonly formStoreService: FormStoreService,
    private readonly changeDetectorRef: ChangeDetectorRef,
    private readonly errorService: ErrorService,
    private readonly confirmDialogService: ConfirmDialogService
  ) {
    super();
  }

  public ngOnInit(): void {
    this.form$ = this.formStoreService.select((state) => state.forms[this.index]);
    this.loading$ = this.formStoreService.select((state) => state.forms[this.index]?.loading);

    this.nomenclatureTreeService
      .findTree({ listStyle: ListStyleEnum.FLAT })
      .subscribe((nomenclatures) => {
        this.nomenclatures = nomenclatures;
        this.changeDetectorRef.markForCheck();
      });

    if (this.formStoreService.getState.forms[this.index]?.isEdit) {
      this.loadDealType();
    }
  }

  public loadDealType(): void {
    const entityId = this.formStoreService.getState.forms[this.index]?.entityId;
    if (!entityId) {
      return;
    }

    this.formStoreService.setLoading(true, this.index);
    this.dealTypeService.findOne(entityId, { withDeleted: true }).subscribe({
      next: (dealType) => {
        this.dealType = {
          id: dealType.id,
          name: dealType.name,
          defaultCompany: dealType.defaultCompany ? { id: dealType.defaultCompany.id } : undefined,
          defaultNomenclatures: dealType.defaultNomenclatures?.length
            ? dealType.defaultNomenclatures?.map((nomenclature) => ({ id: nomenclature.id }))
            : undefined,
          responsible: dealType.responsible ? { id: dealType.responsible.id } : undefined,
          auditorUsers: dealType.auditorUsers?.length
            ? dealType.auditorUsers.map((user) => ({ id: user.id }))
            : undefined,
          auditorGroups: dealType.auditorGroups?.length
            ? dealType.auditorGroups.map((group) => ({ id: group.id }))
            : undefined,
          eavEntityType: dealType.eavEntityType ? { id: dealType.eavEntityType.id } : undefined,
          deleted: dealType.deleted ?? null,
          isRentDefault: dealType.isRentDefault ?? false
        };

        this.formStoreService.setLoading(false, this.index);
        this.errors = null;
        this.changeDetectorRef.markForCheck();
      },
      error: () => {
        this.formStoreService.setLoading(false, this.index);
        this.changeDetectorRef.markForCheck();
      }
    });
  }

  public saveDealType(): void {
    const validationResult = this.checkValidate(this.dealType);
    if (!validationResult.valid) return;

    this.formStoreService.setLoading(true, this.index);

    const saveRequest$ = this.dealType.id
      ? this.dealTypeService.updateOne(this.dealType.id, this.dealType)
      : this.dealTypeService.createOne(this.dealType);

    saveRequest$.subscribe({
      next: (dealType) => {
        this.formStoreService.setLoading(false, this.index);
        this.errorService.addSuccessMessage(
          this.dealType.id ? `Тип сделки "${dealType.name}" обновлен` : 'Тип сделки создан'
        );
        this.formStoreService.saveNext(dealType, this.index);
      },
      error: () => {
        this.formStoreService.setLoading(false, this.index);
      }
    });
  }

  public deleteDealType(): void {
    if (!this.dealType.id) {
      return;
    }

    this.confirmDialogService.confirm({
      message: `Вы действительно хотите удалить тип сделки "${this.dealType.name}"?`,
      accept: () => {
        this.formStoreService.setLoading(true, this.index);
        this.changeDetectorRef.markForCheck();

        this.dealTypeService.deleteOne(this.dealType.id).subscribe({
          next: (dealType) => {
            this.errorService.addSuccessMessage(`Тип сделки "${dealType.name}" удален`);
            this.formStoreService.saveNext(dealType, this.index);
          },
          error: () => {
            this.formStoreService.setLoading(false, this.index);
            this.changeDetectorRef.markForCheck();
          }
        });
      }
    });
  }

  public restoreDealType(): void {
    if (!this.dealType.id) {
      return;
    }

    this.confirmDialogService.confirm({
      message: `Вы действительно хотите восстановить тип сделки "${this.dealType.name}"?`,
      accept: () => {
        this.formStoreService.setLoading(true, this.index);
        this.changeDetectorRef.markForCheck();

        this.dealTypeService.restoreOne(this.dealType.id).subscribe({
          next: (dealType) => {
            this.dealType.deleted = null;
            this.errorService.addSuccessMessage(`Тип сделки "${dealType.name}" восстановлен`);
            this.formStoreService.saveNext(dealType, this.index);
          },
          error: () => {
            this.formStoreService.setLoading(false, this.index);
            this.changeDetectorRef.markForCheck();
          }
        });
      }
    });
  }
}
